import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { DividendsPageView } from './DividendsPageView';
import { useDividendsPage } from './useDividendsPage';

/**
 * `/accounting/dividends` — Profit & Dividends.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner Content
 * child, since React does not catch a component's own throws (the repo's
 * self-served page pattern).
 */
export const DividendsPage: React.FC = () => {
    const page = useDividendsPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('dividends.error.title'),
            errorMessage: t('dividends.error.message'),
            errorRetry: t('dividends.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('dividends-page')}
        >
            <ErrorBoundary
                data-testid="dividends-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="dividends-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <DividendsPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type DividendsPageContentProps = {
    page: ReturnType<typeof useDividendsPage>;
};

const DividendsPageContent: React.FC<DividendsPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its summary labels, toolbar and CTAs while loading and
    // skeletons only the values and rows — the repo's loading-state standard.
    return (
        <DividendsPageView
            figures={page.figures}
            isLoading={page.isLoading || page.figures === null}
            page={page}
        />
    );
};
